class PackSelector extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.variants = [];
    this.options = [];
    this.swatchImages = {};
    this.selectedPack = null;
    this.slotSelections = [];
    this.isAdding = false;
  }

  getThemeRoutes() {
    if (typeof routes !== 'undefined') return routes;
    return window.routes || {};
  }

  getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  getProductForm() {
    const formId = this.dataset.productFormId || `product-form-${this.dataset.sectionId}`;
    return (
      document.getElementById(formId) ||
      document.querySelector('product-form form[data-type="add-to-cart-form"]')
    );
  }

  cartApiUrl(route) {
    const base = route || this.getThemeRoutes().cart_add_url || '/cart/add';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  isGokwikActive() {
    return typeof window.isGokwikCartActive === 'function' && window.isGokwikCartActive();
  }

  buildFetchConfig() {
    if (typeof fetchConfig === 'function') {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      return config;
    }

    return {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    };
  }

  buildCartFormData(payload, includeSections = false) {
    const formData = new FormData();
    const productForm = this.getProductForm();

    if (productForm) {
      productForm.querySelectorAll('input[type="hidden"]').forEach((input) => {
        if (!input.name || input.name === 'id' || input.name.startsWith('properties[')) return;
        if (input.disabled) return;
        formData.append(input.name, input.value);
      });
    } else {
      formData.append('form_type', 'product');
    }

    formData.set('id', String(payload.id));
    formData.set('quantity', String(payload.quantity || 1));

    if (payload.properties) {
      Object.entries(payload.properties).forEach(([key, value]) => {
        if (value != null && value !== '') {
          formData.set(`properties[${key}]`, String(value));
        }
      });
    }

    if (includeSections) {
      const cart = this.getCartElement();
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.set(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.set('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }
      }
    }

    return formData;
  }

  cartErrorMessage(data, fallback) {
    return (
      data?.description ||
      data?.message ||
      (typeof data?.errors === 'string' ? data.errors : null) ||
      fallback
    );
  }

  async postCartAdd(payload, includeSections = false) {
    const themeRoutes = this.getThemeRoutes();
    const cartAddUrl = themeRoutes.cart_add_url;

    if (!cartAddUrl) {
      throw new Error('Cart routes are not available on this page.');
    }

    if (this.isGokwikActive()) {
      includeSections = false;
    }

    const attempts = [
      { payload, includeSections },
      { payload, includeSections: false },
    ];

    let lastError = new Error('Could not add to cart.');

    for (const attempt of attempts) {
      try {
        const config = this.buildFetchConfig();
        config.body = this.buildCartFormData(attempt.payload, attempt.includeSections);

        const response = await fetch(cartAddUrl, config);
        const data = await response.json();

        if (data.status) {
          throw new Error(this.cartErrorMessage(data, 'Could not add to cart.'));
        }

        return data;
      } catch (error) {
        lastError = error;
      }
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'pack-selector',
        productVariantId: String(payload.id),
        errors: lastError.message,
        message: lastError.message,
      });
    }

    throw lastError;
  }

  async postCartAddBulk(items) {
    const cartAddUrl = this.cartApiUrl(this.getThemeRoutes().cart_add_url);

    const payload = {
      items: items.map((item) => ({
        id: parseInt(item.id, 10),
        quantity: item.quantity || 1,
        properties: item.properties || {},
      })),
    };

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (data.status) {
      throw new Error(this.cartErrorMessage(data, 'Could not add to cart.'));
    }

    return data;
  }

  async postCartAddJson(item) {
    const cartAddUrl = this.cartApiUrl(this.getThemeRoutes().cart_add_url);

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: parseInt(item.id, 10),
        quantity: item.quantity || 1,
        properties: item.properties || {},
      }),
    });

    const data = await response.json();

    if (data.status) {
      throw new Error(this.cartErrorMessage(data, 'Could not add to cart.'));
    }

    return data;
  }

  async addItemsForGokwik(items) {
    for (const item of items) {
      await this.postCartAddJson(item);
    }

    await new Promise((resolve) => setTimeout(resolve, 150));

    if (typeof window.notifyGokwikCartRefresh === 'function') {
      window.notifyGokwikCartRefresh();
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    await new Promise((resolve) => setTimeout(resolve, 200));

    if (typeof window.openGokwikSidebarCart === 'function') {
      window.openGokwikSidebarCart();
    }

    if (typeof window.applyGokwikPackGrouping === 'function') {
      window.applyGokwikPackGrouping();
    }
  }

  hasCartSections(response) {
    return Boolean(response?.sections && typeof response.sections === 'object');
  }

  handleCartResponse(response, variantId) {
    const gokwikActive = this.isGokwikActive();
    const hasSections = this.hasCartSections(response);

    if (!gokwikActive && hasSections) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'pack-selector',
          productVariantId: variantId,
          cartData: response,
        });
      }

      const cart = this.getCartElement();
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(response);
        cart.classList.remove('is-empty');
        return Promise.resolve();
      }
    }

    if (typeof window.refreshCartUI === 'function') {
      return window.refreshCartUI({ openDrawer: true });
    }

    return Promise.resolve();
  }

  connectedCallback() {
    this.config = this.readJson('[data-pack-config]');
    this.variants = this.readJson('[data-pack-variants]') || [];
    this.variantMap = this.readJson('[data-pack-variant-map]') || {};
    this.options = this.readJson('[data-pack-options]') || [];
    this.swatchImages = this.readJson('[data-pack-swatch-images]') || {};

    this.variants = this.variants.map((variant) => ({
      ...variant,
      id: String(variant.id),
    }));

    this.variantMap = Object.fromEntries(
      Object.entries(this.variantMap || {}).map(([key, entry]) => [
        String(key).trim(),
        { ...entry, id: entry?.id != null ? String(entry.id) : null },
      ])
    );

    if (!this.config?.packs?.length) {
      this.config = {
        option_name: this.dataset.optionName || 'Color',
        packs: [
          { size: 1, label: '1 unit', discount_percent: 0, badge: null },
          { size: 2, label: '2-pack', discount_percent: 15, badge: 'Most popular' },
          { size: 3, label: '3-pack', discount_percent: 20, badge: null },
        ],
      };
    }

    this.cardsEl = this.querySelector('[data-pack-cards]');
    this.unitsEl = this.querySelector('[data-pack-units]');
    this.totalEl = this.querySelector('[data-pack-total]');
    this.submitBtn = this.querySelector('[data-pack-add-to-cart]');
    this.errorEl = this.querySelector('[data-pack-error]');

    this.optionName = this.dataset.optionName;
    this.optionPosition = parseInt(this.dataset.optionPosition, 10) || 1;

    const targetOption = this.getTargetOption();
    if (targetOption) {
      this.optionName = targetOption.name;
      this.optionPosition = targetOption.position;
    }

    this.swatchShape = this.dataset.swatchShape || 'circle';
    this.baseUnitPrice = parseInt(this.dataset.baseUnitPrice, 10) || 0;
    this.applyPackDiscounts = this.dataset.applyPackDiscounts === 'true';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.productTitle = this.dataset.productTitle || '';

    this.renderPackCards();
    this.submitBtn?.addEventListener('click', () => this.addToCart());

    const defaultPack =
      this.config.packs.find((p) => p.badge === 'Most popular') || this.config.packs[0];
    if (defaultPack) this.selectPack(defaultPack.size);
  }

  readJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('PackSelector: invalid JSON', selector, e);
      return null;
    }
  }

  normalizeOptionName(name) {
    return String(name || '').trim().toLowerCase();
  }

  optionNamesMatch(a, b) {
    const left = this.normalizeOptionName(a);
    const right = this.normalizeOptionName(b);
    if (!left || !right) return false;
    if (left === right) return true;
    if ((left === 'shades' && right === 'shade') || (left === 'shade' && right === 'shades')) return true;
    if ((left === 'color' && right === 'colour') || (left === 'colour' && right === 'color')) return true;
    return false;
  }

  getTargetOption() {
    const preferredNames = [
      this.config?.option_name,
      this.dataset.configOptionName,
      this.dataset.optionName,
    ].filter(Boolean);

    for (const name of preferredNames) {
      const exact = this.options.find((option) => option.name === name);
      if (exact) return exact;

      const caseInsensitive = this.options.find(
        (option) => this.normalizeOptionName(option.name) === this.normalizeOptionName(name)
      );
      if (caseInsensitive) return caseInsensitive;

      const alias = this.options.find((option) => this.optionNamesMatch(option.name, name));
      if (alias) return alias;
    }

    return (
      this.options.find((option) => /shade|color|colour/i.test(option.name)) ||
      this.options.find((option) => option.position === this.optionPosition) ||
      this.options[0]
    );
  }

  findVariantForValue(valueName) {
    const key = String(valueName || '').trim();
    const mapped =
      this.variantMap?.[key] ||
      Object.entries(this.variantMap || {}).find(([mapKey]) => mapKey.trim() === key)?.[1];

    if (mapped?.id) {
      return {
        id: String(mapped.id),
        price: mapped.price,
        available: mapped.available,
        option_value_id: mapped.option_value_id,
        featured_media_id: mapped.featured_media_id,
      };
    }

    const pos = this.optionPosition;
    const variant = this.variants.find((v) => {
      const optionValue =
        pos === 1 ? v.option1 : pos === 2 ? v.option2 : pos === 3 ? v.option3 : null;
      return String(optionValue || '').trim() === key;
    });

    return variant ? { ...variant, id: String(variant.id) } : null;
  }

  getVariantSelectsElement() {
    return document.getElementById(`variant-selects-${this.dataset.sectionId}`);
  }

  getThemeVariantInputForValue(optionValueName) {
    const variantSelects = this.getVariantSelectsElement();
    if (!variantSelects) return null;

    const value = String(optionValueName || '').trim();
    const targetOption = this.getTargetOption();
    const optionIndex = (targetOption?.position || this.optionPosition) - 1;
    const optionGroups = variantSelects.querySelectorAll('fieldset, .product-form__input');

    if (optionGroups[optionIndex]) {
      const scopedInput = optionGroups[optionIndex].querySelector(
        `input[type="radio"][value="${CSS.escape(value)}"]`
      );
      if (scopedInput) return scopedInput;
    }

    return (
      Array.from(variantSelects.querySelectorAll('input[type="radio"]')).find(
        (radio) => String(radio.value).trim() === value
      ) || null
    );
  }

  updateProductFormVariantId(variantId) {
    const form = this.getProductForm();
    if (!form || !variantId) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variantId);
  }

  updateProductMediaByVariantId(variantId) {
    const variant = this.getVariantById(variantId);
    const mapped = Object.values(this.variantMap || {}).find(
      (entry) => String(entry?.id) === String(variantId)
    );
    const mediaId =
      variant?.featured_media?.id ||
      variant?.featured_media_id ||
      mapped?.featured_media_id ||
      null;
    const sectionId = this.dataset.sectionId;

    // Prefer Bindt variant-group filter (shows only that shade's media set)
    if (typeof window.BindtVariantMedia?.filter === 'function') {
      window.BindtVariantMedia.filter(variantId, mediaId, sectionId);
      return;
    }

    if (!mediaId) return;

    const mediaGallery =
      document.querySelector(`media-gallery[data-section="${sectionId}"]`) ||
      document.getElementById(`MediaGallery-${sectionId}`) ||
      document.querySelector('media-gallery');

    if (typeof mediaGallery?.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(`${sectionId}-${mediaId}`, true);
    }
  }

  syncProductVariantFromPackSelection(optionValueName, variantId) {
    const themeInput = this.getThemeVariantInputForValue(optionValueName);

    if (themeInput) {
      if (!themeInput.checked) {
        themeInput.checked = true;
        themeInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Already checked on first paint — still refresh gallery (avoids duplicate thumbs)
        this.updateProductMediaByVariantId(variantId);
      }
      return;
    }

    const variantSelects = this.getVariantSelectsElement();
    if (variantSelects) {
      const select = variantSelects.querySelector('select');
      const value = String(optionValueName || '').trim();
      const option = select
        ? Array.from(select.options).find((opt) => String(opt.value).trim() === value)
        : null;

      if (select && option) {
        if (select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.updateProductMediaByVariantId(variantId);
        }
        return;
      }
    }

    this.updateProductFormVariantId(variantId);
    this.updateProductMediaByVariantId(variantId);
  }

  resolveCartVariantId(variantId) {
    const id = String(variantId || '').trim();
    return /^\d+$/.test(id) ? id : null;
  }

  async addItemsSequentially(items) {
    let lastResponse = null;
    let lastVariantId = null;

    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      const includeSections = !this.isGokwikActive() && index === items.length - 1;
      lastResponse = await this.postCartAdd(item, includeSections);
      lastVariantId = item.id;
    }

    if (lastResponse) {
      await this.handleCartResponse(lastResponse, lastVariantId);
    }
  }

  getVariantById(variantId) {
    const id = String(variantId);
    return this.variants.find((variant) => String(variant.id) === id) || null;
  }

  isValueAvailable(valueName) {
    const variant = this.findVariantForValue(valueName);
    return Boolean(variant?.available && variant?.id);
  }

  formatMoney(cents) {
    const format = this.moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}');

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }

    const amount = String(Math.round(cents / 100));
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amount);
  }

  getDiscountMultiplier(pack) {
    if (!this.applyPackDiscounts) return 1;
    return 1 - (pack.discount_percent || 0) / 100;
  }

  calcPackPreview(pack) {
    const subtotal = this.baseUnitPrice * pack.size;
    const total = Math.round(subtotal * this.getDiscountMultiplier(pack));
    const unit = Math.round(total / pack.size);
    return { subtotal, total, unit };
  }

  calcSelectedTotal() {
    if (!this.selectedPack) return 0;
    const pack = this.config.packs.find((p) => p.size === this.selectedPack);
    if (!pack) return 0;

    let subtotal = 0;
    for (const slot of this.slotSelections) {
      if (!slot.variantId) return null;
      const variant = this.getVariantById(slot.variantId);
      if (!variant) return null;
      subtotal += variant.price;
    }

    return Math.round(subtotal * this.getDiscountMultiplier(pack));
  }

  renderPackCards() {
    if (!this.cardsEl || !this.config?.packs) return;
    this.cardsEl.innerHTML = '';

    this.config.packs.forEach((pack) => {
      const { subtotal, total, unit } = this.calcPackPreview(pack);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pack-card';
      btn.dataset.packSize = pack.size;
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');

      const saveBadge =
        this.applyPackDiscounts && pack.discount_percent > 0
          ? `<span class="pack-card__badge pack-card__badge--sale">Save ${pack.discount_percent}%</span>`
          : '';

      const comparePrice =
        this.applyPackDiscounts && subtotal > total
          ? `<span class="pack-card__compare">${this.formatMoney(subtotal)}</span>`
          : '';

      const popularBadge = pack.badge
        ? `<span class="pack-card__popular">${pack.badge}</span>`
        : '';

      btn.innerHTML = `
        ${popularBadge}
        ${saveBadge}
        <p class="pack-card__label">${pack.label}</p>
        ${comparePrice}
        <span class="pack-card__unit-price">${this.formatMoney(unit)} / unit</span>
        <p class="pack-card__total">${this.formatMoney(total)}</p>
      `;

      btn.addEventListener('click', () => this.selectPack(pack.size));
      this.cardsEl.appendChild(btn);
    });
  }

  selectPack(size) {
    this.selectedPack = size;
    this.hideError();

    this.cardsEl?.querySelectorAll('.pack-card').forEach((card) => {
      const isSelected = parseInt(card.dataset.packSize, 10) === size;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    this.renderUnitSlots(size);
    this.updateTotal();
  }

  renderUnitSlots(count) {
    if (!this.unitsEl) return;
    this.unitsEl.hidden = false;
    this.unitsEl.innerHTML = '';
    this.slotSelections = [];

    const targetOption = this.getTargetOption();
    if (!targetOption) return;

    for (let i = 0; i < count; i += 1) {
      const slot = { index: i, value: null, variantId: null };
      this.slotSelections.push(slot);

      const unit = document.createElement('div');
      unit.className = 'pack-unit';
      unit.innerHTML = `
        <h3 class="pack-unit__title">Unit ${i + 1}</h3>
        <span class="pack-unit__label">${targetOption.name}</span>
        <div class="pack-unit__swatches" data-slot="${i}"></div>
      `;

      const swatchesEl = unit.querySelector('[data-slot]');
      targetOption.values.forEach((value) => {
        const variant = this.findVariantForValue(value.name);
        const available = variant && variant.available;
        const swatch = document.createElement('div');
        swatch.className = `pack-swatch${available ? '' : ' is-unavailable'}`;

        const inputId = `pack-${this.dataset.sectionId}-slot-${i}-${value.name.replace(/\s+/g, '-')}`;
        const bg = this.getSwatchBackground(variant, value);
        const variantId = variant?.id || '';

        swatch.innerHTML = `
          <input
            type="radio"
            name="pack-slot-${this.dataset.sectionId}-${i}"
            id="${inputId}"
            value="${value.name.replace(/"/g, '&quot;')}"
            data-variant-id="${variantId}"
            ${available ? '' : 'disabled'}
          >
          <label for="${inputId}" class="pack-swatch__label-wrap">
            <span
              class="pack-swatch__shape pack-swatch__shape--${this.swatchShape}"
              style="${bg}"
            ></span>
            <span class="pack-swatch__name">${value.name.replace(/</g, '&lt;')}</span>
          </label>
        `;

        const input = swatch.querySelector('input');
        input.addEventListener('change', () => {
          if (!input.checked) return;
          slot.value = value.name;
          slot.variantId = input.dataset.variantId || this.findVariantForValue(value.name)?.id || null;

          if (i === 0) {
            this.syncProductVariantFromPackSelection(value.name, slot.variantId);
          }

          this.updateTotal();
        });

        swatchesEl.appendChild(swatch);
      });

      const firstAvailableInput = swatchesEl.querySelector('input[type="radio"]:not([disabled])');
      if (firstAvailableInput) {
        firstAvailableInput.checked = true;
        firstAvailableInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.unitsEl.appendChild(unit);
    }
  }

  getSwatchBackground(variant, value) {
    if (variant && this.swatchImages[String(variant.id)]) {
      return `background-image: url('${this.swatchImages[String(variant.id)]}');`;
    }
    if (value.swatch_image) {
      return `background-image: url('${value.swatch_image}');`;
    }
    if (value.swatch_color) {
      return `background-color: rgb(${value.swatch_color});`;
    }
    return '';
  }

  updateTotal() {
    const total = this.calcSelectedTotal();
    const allSelected =
      this.slotSelections.length > 0 &&
      this.slotSelections.every((s) => {
        const variant = this.getVariantById(s.variantId);
        return variant && variant.available;
      });

    if (total === null) {
      this.totalEl.textContent = '—';
      this.submitBtn.disabled = true;
      return;
    }

    this.totalEl.textContent = this.formatMoney(total);
    this.submitBtn.disabled = !allSelected || this.isAdding;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async addToCart() {
    if (this.isAdding) return;

    const pack = this.config.packs.find((p) => p.size === this.selectedPack);
    if (!pack) return;

    for (const slot of this.slotSelections) {
      const selected = this.findVariantForValue(slot.value);
      const cartVariantId = this.resolveCartVariantId(selected?.id || slot.variantId);
      if (!selected || !selected.available || !cartVariantId) {
        this.showError('Please select an available shade for each unit.');
        return;
      }
      slot.variantId = cartVariantId;
    }

    this.isAdding = true;
    this.submitBtn.disabled = true;
    this.hideError();

    const packKey =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `pack-${Date.now()}`;

    const items = this.slotSelections.map((slot, index) => ({
      id: slot.variantId,
      quantity: 1,
      properties: {
        _pack_key: packKey,
        _pack_size: String(pack.size),
        _pack_label: pack.label,
        _pack_slot: String(index + 1),
        _pack_shade: slot.value,
      },
    }));

    try {
      if (this.isGokwikActive()) {
        await this.addItemsForGokwik(items);
      } else {
        try {
          const response = await this.postCartAddBulk(items);
          await this.handleCartResponse(response, items[items.length - 1].id);
        } catch (bulkError) {
          await this.addItemsSequentially(items);
        }
      }
    } catch (error) {
      this.showError(error.message || 'Could not add to cart.');
      console.error('PackSelector addToCart failed', {
        items,
        variantMap: this.variantMap,
        productFormFound: Boolean(this.getProductForm()),
        cartAddUrl: this.getThemeRoutes().cart_add_url,
        error,
      });
    } finally {
      this.isAdding = false;
      this.updateTotal();
    }
  }
}

customElements.define('pack-selector', PackSelector);